import { Modal, Input, P, Gap } from '@salecycle/ui-component-library';
import { RestApiError } from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useParams } from 'react-router';
import { resetSftpUserPassword } from '../sections/queries/queries';
import { emailValidCheck } from '../utils/helpers';

interface IProps {
  passwordResetModalOpen: boolean;
  setPasswordResetModalOpen(isOpen: boolean): void;
  currentEmail: string;
  setCurrentEmail(email: string): void;
}

export const SftpPasswordResetModal = ({
  passwordResetModalOpen,
  setPasswordResetModalOpen,
  currentEmail,
  setCurrentEmail,
}: IProps) => {
  const { t } = useTranslation('sftpUserManagement');
  const { username } = useParams<{ username: string }>();
  const [email, setEmail] = useState(currentEmail);
  const [emailValid, setEmailValid] = useState(true);
  const { mutate } = useMutation(resetSftpUserPassword);
  const { showNotification } = useNotifications();

  const handleClose = () => {
    setEmail(currentEmail);
    setEmailValid(true);
    setPasswordResetModalOpen(false);
  };

  useEffect(() => {
    setEmail(currentEmail);
  }, [currentEmail]);

  const handleEmailChange = (value: string) => {
    const isValid = emailValidCheck(value);
    setEmail(value);
    setEmailValid(isValid);
  };

  const handleResetPassword = async () => {
    const updatedSftpUser = {
      username,
      email,
    };
    mutate(updatedSftpUser, {
      onSuccess: data => {
        showNotification(`${t('editSftpUser.modal.password.reset.success', { email })}`);
      },
      onError: (error: RestApiError, variables, context) => {
        showNotification(
          t('editSftpUser.modal.password.reset.failure', {
            error: error.response.data.errors.join(),
          }),
          'error',
        );
      },
    });
    setCurrentEmail(email);
    setPasswordResetModalOpen(false);
  };

  return (
    <Modal
      heading={t('editSftpUser.modal.heading', { username })}
      variation="clean"
      isOpen={passwordResetModalOpen}
      onCancelButtonClicked={handleClose}
      onCloseButtonClicked={handleClose}
      onOkButtonClicked={handleResetPassword}
      okButtonLabel={t('editSftpUser.modal.password.reset.resend')}
      cancelButtonLabel={t('editSftpUser.modal.password.reset.cancel')}
      okButtonDisabled={!emailValid}
    >
      <P>{t('editSftpUser.modal.password.reset.text1')}</P>
      <P>{t('editSftpUser.modal.password.reset.text2')}</P>
      <Gap top={20} />

      <Input
        ariaLabel={t('editSftpUser.modal.email.label')}
        type="email"
        value={email}
        placeholder={t('editSftpUser.modal.email.placeholder')}
        onChange={handleEmailChange}
        validationState={emailValid ? 'complete' : 'warning'}
        validationMessage={!emailValid ? t('editSftpUser.modal.password.reset.validation') : null}
      />
    </Modal>
  );
};
